import { type WithContextualField } from '@autodromo/contextual-fields'
import { CSSProperties, ReactNode } from "react"
import { sanitizeSvg } from "../../utils/sanitize"
import { ButtonDefault } from "./Button.styles"

export interface ButtonProps extends WithContextualField {
  children?: ReactNode
  loading?: boolean
  disabled?: boolean
  rightIcon?: string
  leftIcon?: string
  iconColor?: string
  type: 'submit' | 'button' | 'link'
  size?: 'small' | 'medium' | 'full'
  href?: string
  target?: string
  palette?: {
    bgColor?: string
    borderColor?: string
    textColor?: string
  }
  onClick?: () => void
  dataset?: { [key: string]: any }
  className?: string
}

export const Button = ({
  children,
  loading,
  disabled,
  rightIcon,
  leftIcon,
  iconColor,
  type = 'button',
  size,
  href = '',
  target = '_self',
  palette,
  dataset,
  onClick,
  className,
  ...contextual
}: ButtonProps) => {

  const isBlocked = Boolean(disabled || loading)

  const iconStyle = {
    '--icon-color': iconColor || palette?.textColor,
  } as CSSProperties

  const ContentWithIcons = () => (
    <>
      { leftIcon &&
        <span
          style={iconStyle}
          dangerouslySetInnerHTML={{__html: sanitizeSvg(leftIcon)}}
        ></span>
      }

      {children}

      { rightIcon &&
        <span
          style={iconStyle}
          dangerouslySetInnerHTML={{__html: sanitizeSvg(rightIcon)}}
        ></span>
      }
    </>
  )

  return (
    <>
      {type === 'link' &&
        <ButtonDefault
        className={className}
        as="a"
        href={isBlocked ? undefined : href}
        target={target}
        palette={palette}
        size={size}
        {...contextual}
        aria-disabled={isBlocked ? true : undefined}
        {...(dataset && Object.keys(dataset).length > 0
          ? Object.fromEntries(
            Object.entries(dataset).map(([key, value]) => [
              `data-${key}`,
              value,
            ])
          )
          : {})}
        >
          <ContentWithIcons />
        </ButtonDefault>
      }

      {(type === 'submit' || type === 'button') &&
        <ButtonDefault
          className={className}
          {...contextual}
          as='button'
          type={type}
          palette={palette}
          size={size}
          disabled={isBlocked}
          aria-busy={loading ? true : undefined}
          onClick={isBlocked ? undefined : onClick}
          {...(dataset && Object.keys(dataset).length > 0
            ? Object.fromEntries(
                Object.entries(dataset).map(([key, value]) => [
                  `data-${key}`,
                  value,
                ])
              )
            : {})}
        >
          <ContentWithIcons />
        </ButtonDefault>
      }
    </>
  )
}
